/**
 * Projekt-Detail — Aufgaben, Tätigkeiten und verbrauchte Token-Einheiten (TQ #341).
 *
 * GET /api/org/:slug/projects/:name -> { project_name, status, tasks, timesheet_logs, token_bookings }.
 * timesheet_logs_unavailable=true: die Zeiterfassung war nicht lesbar (TQ #790), das Detail kommt ohne Zeitlogs.
 * Feldnamen von platform-workspace bestaetigt (07.10.2026, main @ 91dcb35):
 *   tasks: { name, project, status, progress (int), exp_start_date, exp_end_date }
 *   timesheet_logs: { timesheet, activity_type, from_time, to_time, hours (float), description }
 *   token_bookings: { datum, typ, einheiten_mio (float), kategorie, projekt? }
 *     — KEIN notizen-Feld (TQ #509, interner Prozesstext, nicht kundenseitig anzeigen).
 * 404 wenn das Projekt nicht zur Org gehoert (Tenant-Isolation, kein Existence-Leak).
 */
import { useEffect, useState } from 'react'
import { useParams, Link, useLocation } from 'react-router-dom'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { useToast } from '../components/ui'
import { projectTitle } from '../lib/projectStatus'

interface Organization {
  id: number
  name: string
  slug: string
  public_token?: string
  member_role: string
}

interface ProjectTask {
  name: string
  project: string
  status: string
  progress: number
  exp_start_date?: string
  exp_end_date?: string
}

interface TimesheetLog {
  timesheet: string
  activity_type: string
  from_time: string
  to_time: string
  hours: number
  description?: string
}

interface TokenBooking {
  datum: string
  typ: 'COGS' | 'Labor' | string
  einheiten_mio: number
  kategorie: string
  projekt?: string
}

interface ProjectDetailResponse {
  project_name?: string
  status?: string
  timesheet_logs_unavailable?: boolean
  tasks: ProjectTask[]
  timesheet_logs: TimesheetLog[]
  token_bookings: TokenBooking[]
  unavailable?: string
}

interface Props {
  organization: Organization
}

const fmtDate = (s?: string) =>
  s ? new Date(s).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–'

const fmtEinheiten = (mio: number) => `${(Math.round(mio * 100) / 100).toLocaleString('de-DE')} Mio Token`

export default function ProjectDetail({ organization }: Props) {
  const { name } = useParams<{ name: string }>()
  const { api: keycloakApi } = useAuth()
  const { showToast } = useToast()
  const slugOrToken = organization.public_token || organization.slug
  // Pfad-basiert statt relativer ".."-Links (siehe TokenHistory.tsx) — damit
  // unabhaengig von der React-Router-"route relative"-Verschachtelungstiefe
  // korrekt, und funktioniert sowohl im Slug-Cockpit als auch auf Custom-Domain.
  const { pathname } = useLocation()
  const orgBase = pathname.replace(/\/projekte\/[^/]+\/?$/, '')

  const [data, setData] = useState<ProjectDetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  // Backend meldet unavailable=no_access, wenn ERPNext dem Read-Bot das Lesen verweigert (TQ #783).
  const unavailable = data?.unavailable === 'no_access'

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setNotFound(false)
      try {
        const r = await keycloakApi!.get(`/api/org/${slugOrToken}/projects/${encodeURIComponent(name ?? '')}`)
        if (cancelled) return
        setData(r.data ?? null)
      } catch (e) {
        if (cancelled) return
        const err = e as { response?: { status?: number; data?: { error?: string } } }
        if (err.response?.status === 404) {
          setNotFound(true)
        } else {
          showToast('error', `Projekt konnte nicht geladen werden: ${err.response?.data?.error ?? 'Unbekannt'}`)
        }
        setData(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [slugOrToken, name, keycloakApi, showToast])

  if (loading) {
    return <p style={{ padding: '1.5rem', color: '#6b7280' }}>Lade …</p>
  }

  if (unavailable) {
    return (
      <div className="page">
        <div className="page-header">
          <div>
            <Link to={`${orgBase}/projekte`} style={{ color: '#6b7280', fontSize: '0.875rem', textDecoration: 'none' }}>← Zurück zu Projekte</Link>
            <h1 style={{ marginTop: 4 }}>{name}</h1>
          </div>
        </div>
        <div role="status" style={{ padding: '1.25rem 1rem', background: '#f9fafb', borderRadius: 6, color: '#6b7280', textAlign: 'center' }}>
          Projektdaten sind für {organization.name} derzeit nicht abrufbar.
        </div>
      </div>
    )
  }

  if (notFound || !data) {
    return (
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Projekt nicht gefunden</h1>
          </div>
        </div>
        <Link to={`${orgBase}/projekte`}>← Zurück zu Projekte</Link>
      </div>
    )
  }

  const openTasks = data.tasks.filter((t) => t.status !== 'Completed' && t.status !== 'Cancelled')
  const doneTasks = data.tasks.filter((t) => t.status === 'Completed' || t.status === 'Cancelled')

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <Link to={`${orgBase}/projekte`} style={{ color: '#6b7280', fontSize: '0.875rem', textDecoration: 'none' }}>← Zurück zu Projekte</Link>
          <h1 style={{ marginTop: 4 }}>{projectTitle(data.project_name, name ?? '', data.status)}</h1>
          {data.project_name?.trim() && (
            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>{name}</p>
          )}
        </div>
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <Link to={`${orgBase}/dokumente/regieberichte`} style={{ color: '#FE4F00', fontSize: '0.875rem', textDecoration: 'none' }}>
          Monats-Regiebericht im Dokumente-Bereich ansehen →
        </Link>
      </div>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>Aufgaben</h2>
        {data.tasks.length === 0 ? (
          <p style={{ color: '#6b7280' }}>Keine Aufgaben hinterlegt.</p>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                  <th style={{ padding: '0.75rem 1rem' }}>Aufgabe</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>Status</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>Fortschritt</th>
                  <th style={{ padding: '0.75rem 1rem' }}>Zeitraum</th>
                </tr>
              </thead>
              <tbody>
                {[...openTasks, ...doneTasks].map((t) => (
                  <tr key={t.name} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.75rem 1rem' }}>{t.name}</td>
                    <td style={{ padding: '0.75rem 0.5rem', color: '#6b7280' }}>{t.status}</td>
                    <td style={{ padding: '0.75rem 0.5rem', color: '#6b7280' }}>{t.progress}%</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>
                      {fmtDate(t.exp_start_date)} – {fmtDate(t.exp_end_date)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>Letzte Tätigkeiten</h2>
        {data.timesheet_logs_unavailable ? (
          <div role="status" style={{ padding: '1rem', background: '#f9fafb', borderRadius: 6, color: '#6b7280' }}>
            Die Tätigkeiten sind derzeit nicht abrufbar.
          </div>
        ) : data.timesheet_logs.length === 0 ? (
          <p style={{ color: '#6b7280' }}>Keine Tätigkeiten erfasst.</p>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                  <th style={{ padding: '0.75rem 1rem' }}>Datum</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>Tätigkeit</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>Beschreibung</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Aufwand</th>
                </tr>
              </thead>
              <tbody>
                {data.timesheet_logs.map((log, i) => (
                  <tr key={`${log.timesheet}-${i}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{fmtDate(log.from_time)}</td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>{log.activity_type}</td>
                    <td style={{ padding: '0.75rem 0.5rem', color: '#9ca3af' }}>{log.description ?? '–'}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {log.hours.toLocaleString('de-DE')} Std.
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>Verbrauchte Einheiten</h2>
        {data.token_bookings.length === 0 ? (
          <p style={{ color: '#6b7280' }}>Noch keine Token-Buchungen für dieses Projekt.</p>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                  <th style={{ padding: '0.75rem 1rem' }}>Datum</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>Typ</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>Kategorie</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Menge</th>
                </tr>
              </thead>
              <tbody>
                {data.token_bookings.map((b, i) => (
                  <tr key={`${b.datum}-${i}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{fmtDate(b.datum)}</td>
                    <td style={{ padding: '0.75rem 0.5rem', color: '#6b7280' }}>{b.typ}</td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>{b.kategorie || '–'}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtEinheiten(b.einheiten_mio)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
